<template>
  <div
    class="mb-2 d-flex item"
    v-bind:class="{current: isCurrent, trashed: isTrashed}"
    v-bind:style="styles"
    @click="onSelect"
  >
    <div class="d-flex align-items-center flex-shrink-0 pr-2">
      <i v-if="! isEdit" class="far fa-lg"
         :class="{'fa-check-square': isSelected, 'fa-square': !isSelected || isTrashed}" @click.stop="onToggle"></i>
    </div>
    <div class="flex-grow-1">
      <div>
        <span class="item-title">
          <span class="contact" @click.stop="">
              <el-tooltip
                v-if="inDoNotContact"
                effect="light"
                content="Do Not Contact"
                placement="left"
              >
                  <span class="text-danger cursor-default"><i class="fas fa-user-slash"></i></span>
              </el-tooltip>
              <span class="filemaker-id" v-if="filemakerId">{{ filemakerId }} </span>
              <span class="contact-full-name">{{ contactFullName | uppercase }}</span>
              <span v-if="hasContactPhoneNumber" class="contact-phone-number">({{ contactPhoneNumber }})</span>
          </span>
        </span>
      </div>
      <div>
        <span class="item-title">
          <span class="contact" @click.stop="">
            <span v-if="outgoingNumberTitle">{{ outgoingNumberTitle | uppercase }}</span>
            <span class="contact-full-name">({{ outgoingNumberPhoneNumber | uppercase }})</span>
          </span>
        </span>
      </div>
    </div>
    <div class="d-flex flex-shrink-0 align-items-center">
      <div>
        <div>
          <div class="d-flex justify-content-end">
            <el-tooltip content="Make a call" placement="top">
              <outbound-call
                v-if="canMakeCall"
                :conversation="conversation"
                class="btn btn-sm btn-success mr-1"
                :disabled="! hasContactPhoneNumber"
              >
              </outbound-call>
            </el-tooltip>
            <el-tooltip content="Move to group" placement="top">
              <button class="btn btn-sm btn-purple mr-1" @click.stop="onMove">
                <i class="fas fa-arrow-right"></i>
              </button>
            </el-tooltip>
            <el-tooltip content="Filter send lists" placement="top" v-if="! isEdit">
              <button class="btn btn-sm btn-purple mr-1" @click.stop="onFilter">
                <i class="fas fa-filter"></i>
              </button>
            </el-tooltip>
            <el-tooltip content="Private chat" placement="top">
              <button class="btn btn-sm btn-purple mr-1" :disabled="!canSendMessage && !canSendApprovedMessage"
                      @click.stop="onSend">
                <i class="fas fa-paper-plane"></i>
              </button>
            </el-tooltip>
            <el-tooltip content="Edit" placement="top">
              <button class="btn btn-sm btn-purple mr-1" @click.stop="onEdit">
                <i class="fas fa-edit"></i>
              </button>
            </el-tooltip>
            <el-tooltip content="Delete" placement="top">
              <button class="btn btn-sm btn-danger" @click.stop="onDelete">
                <i class="fas fa-times fa-wider"></i>
              </button>
            </el-tooltip>
          </div>
        </div>
        <div class="d-flex justify-content-end pt-1" v-if="expiresIn">
                    <span
                      class="badge"
                      v-bind:class="{'badge-info': expiresIn.isFuture, 'badge-warning': !expiresIn.isFuture}"
                    >
                        {{ expiresIn.text }}
                    </span>
        </div>
      </div>
    </div>
    <div class="trashed-controls" v-if="isTrashed">
      <el-tooltip content="Restore conversation" placement="top">
        <i class="fas fa-redo" @click="onRestore"></i>
      </el-tooltip>
    </div>
  </div>
</template>

<script>
import OutboundCall from '../../call/outbound-call'

export default {
  components: {
    OutboundCall
  },

  props: {
    sendListConversation: Object,
    isCurrent: Boolean,
    isSelected: Boolean,
    canMakeCall: Boolean,
    isEdit: Boolean,
  },

  data() {
    return {};
  },

  computed: {
    /**
     * @return {Object|null}
     */
    conversation() {
      return _.get(this.sendListConversation, 'conversation', null);
    },

    /**
     * @return {Object|null}
     */
    contact() {
      return _.get(this.sendListConversation, 'conversation.contact', null);
    },

    /**
     * @return {Object|null}
     */
    outgoingNumber() {
      return _.get(this.sendListConversation, 'conversation.outgoing_number', null);
    },

    /**
     * @return {String|null}
     */
    filemakerId() {
      return _.get(this.contact, 'filemaker_id', null);
    },

    /**
     * @return {String|null}
     */
    contactFullName() {
      return _.get(this.contact, 'full_name', null);
    },

    /**
     * @return {String|null}
     */
    contactPhoneNumber() {
      return _.get(this.contact, 'phone_number', null);
    },

    /**
     * @return {boolean}
     */
    hasContactPhoneNumber() {
      return Boolean(this.contactPhoneNumber);
    },

    /**
     * @return {String|null}
     */
    outgoingNumberTitle() {
      return _.get(this.outgoingNumber, 'title', null);
    },

    /**
     * @return {String|null}
     */
    outgoingNumberPhoneNumber() {
      return _.get(this.outgoingNumber, 'phone_number', null);
    },

    /**
     * @return {Object|null}
     */
    expiredAt() {
      return _.get(this.sendListConversation, 'expired_at', null);
    },

    /**
     * @return {Object}
     */
    styles() {
      return {};
    },

    expiresIn() {
      if (!this.expiredAt) {
        return null;
      }

      const expiredAt = moment(this.expiredAt.iso);
      const now = moment();
      const result = {
        isFuture: now.isBefore(expiredAt),
        days: expiredAt.diff(now, 'days'),
        text: '',
      };

      if (!result.isFuture) {
        result.text = 'Expired';
      } else if (result.days === 0) {
        result.text = `Expires today`;
      } else if (result.days === 1) {
        result.text = `Expires in a day`;
      } else {
        result.text = `Expires in ${result.days} days`;
      }

      return result;
    },

    /**
     * @returns {boolean}
     */
    inDoNotContact() {
      return _.get(this.conversation, 'do_not_contact', null) !== null;
    },

    /**
     * @return {boolean}
     */
    isTrashed() {
      return Boolean(_.get(this.conversation, 'is_trashed', false));
    },

    /**
     * @returns {boolean}
     */
    canSendMessage() {
      return Boolean(_.get(this.conversation, 'canSendMessage', false));
    },

    /**
     * @return {boolean}
     */
    canSendApprovedMessage() {
      return Boolean(_.get(this.conversation, 'canSendApprovedMessage', false));
    },

    /**
     * @return {String}
     */
    moveUrl() {
      return _.get(this.sendListConversation, 'move_url');
    },
  },

  methods: {
    /**
     * @emits select
     */
    onSelect() {
      this.$emit('select', this.sendListConversation);
    },

    /**
     * @emits delete
     */
    onDelete() {
      this.$emit('delete', this.sendListConversation);
    },

    /**
     * @emits edit
     */
    onEdit() {
      this.$emit('edit', this.sendListConversation);
    },

    /**
     * @emits send-single-message
     */
    onSend() {
      this.$emit('open-chat', {conversation: this.sendListConversation.conversation});
    },

    /**
     * @emits filter
     */
    onFilter() {
      this.$emit('filter', this.sendListConversation.conversation.contact);
    },

    /**
     * @emits toggle
     */
    onToggle() {
      this.$emit('toggle', this.sendListConversation);
    },

    /**
     * @emits restore
     */
    onRestore() {
      this.$emit('restore', this.conversation);
    },

    /**
     * @emits move
     */
    onMove() {
      this.$emit('move', this.moveUrl);
    },
  },
}
</script>
